import React, { useEffect, useState, useCallback } from 'react';
import { Search, Plus } from 'lucide-react';
import api from '../api/client';
import { contactsApi } from '../api/contacts';
import { useToast } from '../hooks/useToast';

interface Contact { is_active?: boolean; address?: string;
  id: number; name: string; mobile: string; email: string;
  ar_balance: number; ap_balance: number; credit_limit: number;
  credit_hold: boolean; roles: string[];
}

const roleBadge: Record<string, string> = {
  CUSTOMER: 'badge-blue', SUPPLIER: 'badge-amber',
  EMPLOYEE: 'badge-purple', INVESTOR: 'badge-green', OTHER: 'badge-gray',
};

const Contacts: React.FC = () => {
  const { showToast } = useToast();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal]       = useState(0);
  const [search, setSearch]     = useState('');
  const [typeFilter, setType]   = useState('');
  const [loading, setLoading]   = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form, setForm] = useState({ name:'', mobile:'', email:'', phone:'', address:'', credit_limit:'0', payment_terms_days:'0', notes:'', roles:['CUSTOMER'] });

  const load = useCallback(() => {
    setLoading(true);
    contactsApi.list({ search, type: typeFilter }).then(res => {
      setContacts(res.data.data);
      setTotal(res.data.total);
      setLoading(false);
    });
  }, [search, typeFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const handleCreate = async () => {
    if (!form.name.trim()) { showToast('Name is required', 'error'); return; }
    setSaving(true);
    try {
      await contactsApi.create({ ...form, credit_limit: Number(form.credit_limit), payment_terms_days: Number(form.payment_terms_days) });
      showToast('Contact created!');
      setShowForm(false);
      setForm({ name:'', mobile:'', email:'', phone:'', address:'', credit_limit:'0', payment_terms_days:'0', notes:'', roles:['CUSTOMER'] });
      load();
    } catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const toggleRole = (role: string) => {
    setForm(f => ({ ...f, roles: f.roles.includes(role) ? f.roles.filter(r => r !== role) : [...f.roles, role] }));
  };
  const [editContact, setEditContact] = useState<any>(null);
  const [editForm, setEditForm] = useState<any>({});
  const handleToggle = async (id: number, isActive: any) => {
    try { await api.patch('/contacts/'+id, { is_active: !isActive }); showToast(isActive ? 'Disabled' : 'Enabled'); load(); }
    catch { showToast('Failed', 'error'); }
  };
  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this contact?')) return;
    try { await api.delete('/contacts/'+id); showToast('Deleted'); load(); }
    catch (err: any) { showToast(err.response?.data?.message || 'Cannot delete', 'error'); }
  };
  const handleEditSave = async () => {
    if (!editContact) return; setSaving(true);
    try { await api.put('/contacts/'+editContact.id, editForm); showToast('Updated'); setEditContact(null); load(); }
    catch (err: any) { showToast(err.response?.data?.message || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Contacts</h1>
        <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}><Plus size={13} /> Add Contact</button>
      </div>

      {showForm && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="card-header"><span>New Contact</span><button className="btn btn-secondary btn-sm" onClick={() => setShowForm(false)}>Cancel</button></div>
          <div className="card-body">
            <div className="form-grid-3">
              <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} placeholder="Contact name" /></div>
              <div className="form-group"><label className="form-label">Mobile</label><input className="form-control" value={form.mobile} onChange={e => setForm(f=>({...f,mobile:e.target.value}))} placeholder="+252..." /></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-control" type="email" value={form.email} onChange={e => setForm(f=>({...f,email:e.target.value}))} placeholder="email@..." /></div>
            </div>
            <div className="form-grid-3">
              <div className="form-group"><label className="form-label">Credit Limit</label><input className="form-control mono" type="number" value={form.credit_limit} onChange={e => setForm(f=>({...f,credit_limit:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Payment Terms (days)</label><input className="form-control mono" type="number" value={form.payment_terms_days} onChange={e => setForm(f=>({...f,payment_terms_days:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Type</label>
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:6 }}>
                  {['CUSTOMER','SUPPLIER','EMPLOYEE','INVESTOR','OTHER'].map(r => (
                    <button key={r} className={`filter-pill ${form.roles.includes(r) ? 'active' : ''}`} onClick={() => toggleRole(r)}>{r}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="form-group"><label className="form-label">Address</label><input className="form-control" value={form.address} onChange={e => setForm(f=>({...f,address:e.target.value}))} /></div>
            <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Saving...' : 'Save Contact'}</button>
          </div>
        </div>
      )}

      <div className="filter-bar">
        <div className="search-box"><Search size={13} color="var(--tx3)" /><input placeholder="Search contacts..." value={search} onChange={e => setSearch(e.target.value)} /></div>
        {['','CUSTOMER','SUPPLIER','EMPLOYEE','INVESTOR'].map(t => (
          <button key={t} className={`filter-pill ${typeFilter===t?'active':''}`} onClick={() => setType(t)}>{t || 'All'}</button>
        ))}
        <span className="record-count">{total} contacts</span>
      </div>

      <div className="table-wrap">
        <table>
          <thead><tr><th>Name</th><th>Mobile</th><th>Email</th><th>Type</th><th style={{textAlign:'right'}}>AR Balance</th><th style={{textAlign:'right'}}>AP Balance</th><th>Status</th></tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{textAlign:'center',padding:32,color:'var(--tx3)'}}>Loading...</td></tr>
            ) : contacts.length === 0 ? (
              <tr><td colSpan={7}><div className="empty-state"><p>No contacts found.</p></div></td></tr>
            ) : contacts.map(c => (
              <tr key={c.id}>
                <td style={{fontWeight:600}}>{c.name}</td>
                <td style={{color:'var(--tx3)'}}>{c.mobile || '-'}</td>
                <td style={{color:'var(--tx3)'}}>{c.email || '-'}</td>
                <td>{c.roles?.map(r => <span key={r} className={`badge ${roleBadge[r]||'badge-gray'}`} style={{marginRight:4}}>{r}</span>)}</td>
                <td className="td-num td-green">{Number(c.ar_balance).toFixed(2)}</td>
                <td className="td-num td-red">{Number(c.ap_balance).toFixed(2)}</td>
                <td><span className={`badge ${Number(c.is_active)===1 ? 'badge-green' : 'badge-gray'}`}>{Number(c.is_active)===1 ? 'Active' : 'Inactive'}</span></td>
                <td style={{display:'flex',gap:4}}>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setEditContact(c); setEditForm({name:c.name,mobile:c.mobile||'',email:c.email||'',address:c.address||''}); }}>Edit</button>
                  <button className="btn btn-secondary btn-sm" onClick={() => handleToggle(c.id, c.is_active)}>
                    {Number(c.is_active)===1 ? 'Disable' : 'Enable'}
                  </button>
                  <button className="btn btn-secondary btn-sm" style={{color:'var(--red)'}} onClick={() => handleDelete(c.id)}>Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editContact && (
        <div style={{position:'fixed',top:0,left:0,right:0,bottom:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div className="card" style={{width:480,padding:24}}>
            <div className="card-header"><span>Edit Contact</span><button className="btn btn-secondary btn-sm" onClick={() => setEditContact(null)}>Cancel</button></div>
            <div className="card-body">
              <div className="form-group"><label className="form-label">Name *</label><input className="form-control" value={editForm.name||''} onChange={e => setEditForm((f:any)=>({...f,name:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Mobile</label><input className="form-control" value={editForm.mobile||''} onChange={e => setEditForm((f:any)=>({...f,mobile:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-control" value={editForm.email||''} onChange={e => setEditForm((f:any)=>({...f,email:e.target.value}))} /></div>
              <div className="form-group"><label className="form-label">Address</label><input className="form-control" value={editForm.address||''} onChange={e => setEditForm((f:any)=>({...f,address:e.target.value}))} /></div>
              <div style={{marginTop:16,display:'flex',gap:8,justifyContent:'flex-end'}}><button className="btn btn-secondary" onClick={() => setEditContact(null)}>Cancel</button><button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button></div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Contacts;